import { useCallback, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Loader from '../../components/atoms/Loader';
import SummaryFilterBar from '../../components/molecules/SummaryFilterBar';
import HealthSystemChartGrid from '../../components/charts/HealthSystemChartGrid';
import { FILTER_TYPES } from '../../constants';
import { fetchAllRequestCount } from '../../services/DashboardService';
import { useSummaryFilters } from '../../hooks/useSummaryFilters';
import type {
  HealthSystemSummaryRow,
  SelectedDateRange,
} from '../../interfaces/Dashboard';

/**
 * Health-Systemwise Summary: the same aggregate data as the Landing page, but
 * rendered as one small column chart per health system instead of a single
 * combined chart. No purpose-of-use filter on this page.
 */
const HealthSystemwiseSummary = () => {
  const filters = useSummaryFilters('healthSystemBreakdown');

  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<HealthSystemSummaryRow[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>(
    {
      fromdate: '',
      todate: '',
      filterType: FILTER_TYPES[0].label,
    },
  );

  const loadSummary = useCallback(
    async (useDefaults = false) => {
      setIsLoading(true);

      const {
        resolved,
        filterType,
        selectedDateRange: dateRange,
      } = filters.resolve(
        useDefaults
          ? {
              filterType: FILTER_TYPES[0],
              dateFilter: { label: 'All', value: 'All' },
            }
          : undefined,
      );

      const response = await fetchAllRequestCount({
        fromdate: resolved.fromdate,
        todate: resolved.todate,
        filterType: filterType.value,
        isFilterByCreatedDate: useDefaults
          ? false
          : filters.filterByRequestDate,
      });

      setSummaryData(response.healthSystemwiseSummaryList);
      setSelectedDateRange(dateRange);
      setIsLoading(false);
    },
    [filters],
  );

  useEffect(() => {
    void loadSummary();
    // Runs once on mount; loadSummary is intentionally not a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    filters.reset();
    void loadSummary(true);
  };

  return (
    <Container className='main-container'>
      <SummaryFilterBar
        filterType={filters.filterType}
        onFilterTypeChange={filters.setFilterType}
        dateFilter={filters.dateFilter}
        onDateFilterChange={filters.handleDateFilterChange}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        onDateChange={filters.handleDateChange}
        errors={filters.errors}
        filterByRequestDate={filters.filterByRequestDate}
        onFilterByRequestDateChange={filters.setFilterByRequestDate}
        onClear={handleClear}
        onApply={() => void loadSummary()}
        isLoading={isLoading}
      />

      {isLoading ? (
        <Grid
          container
          className='all-resouces-list-wrapper request-container'
          spacing={3}
        >
          <Grid size={12}>
            <Paper className='respurce-list-paper' style={{ height: '500px' }}>
              <Loader fullHeight />
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          className='all-resouces-list-wrapper request-container'
          spacing={3}
        >
          <Grid size={12}>
            <Paper className='respurce-list-paper category-paper-wrapper'>
              <Typography
                color='primary'
                variant='h6'
                className='category-name'
              >
                {selectedDateRange.filterType} Summary -{' '}
                <span style={{ color: 'black' }}>
                  From {selectedDateRange.fromdate} To{' '}
                  {selectedDateRange.todate}
                </span>
              </Typography>
            </Paper>
          </Grid>

          <HealthSystemChartGrid
            summaryData={summaryData}
            filterType={filters.filterType.value}
          />
        </Grid>
      )}
    </Container>
  );
};

export default HealthSystemwiseSummary;
